import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, DollarSign, Copy, Check, Plus, ArrowLeft, ExternalLink, RefreshCw,
} from 'lucide-react';
import apiClient from '../lib/apiClient';
import AffiliateClickFunnel from './AffiliateClickFunnel';

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (rate) => `${Math.round((rate || 0) * 100)}%`;

const HEARD_ABOUT_US_LABELS = {
  ai_assistant: 'AI assistant (ChatGPT, Claude, Gemini, etc.)',
  google_search: 'Google search',
  social_media: 'Social media',
  referral: 'Referred by someone',
  other: 'Other',
};

const COMMISSION_STATUS_STYLE = {
  paid: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-yellow-100 text-yellow-700',
  held: 'bg-blue-100 text-blue-700',
  void: 'bg-gray-100 text-gray-500 line-through',
};
const COMMISSION_STATUS_LABEL = {
  held: 'Held (guarantee window)',
  void: 'Void (churned before 2nd payment)',
};
function CommissionStatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${COMMISSION_STATUS_STYLE[status] || 'bg-gray-100 text-gray-600'}`}>
      {COMMISSION_STATUS_LABEL[status] || status}
    </span>
  );
}

const REFERRAL_STATUS_STYLE = {
  active: 'bg-green-100 text-green-700',
  churned: 'bg-gray-100 text-gray-600',
  capped: 'bg-blue-100 text-blue-700',
};
const REFERRAL_STATUS_LABEL = { capped: '12mo cap reached' };
function ReferralStatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${REFERRAL_STATUS_STYLE[status] || 'bg-yellow-100 text-yellow-700'}`}>
      {REFERRAL_STATUS_LABEL[status] || status}
    </span>
  );
}

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
    >
      {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
      {copied ? 'Copied' : label}
    </button>
  );
}

function ApplicationsQueue({ applications, onDecided }) {
  const [rates, setRates] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  const approve = async (id) => {
    setBusyId(id); setError(null);
    try {
      const pct = rates[id];
      await apiClient.post(`/affiliates/${id}/approve`, {
        commission_rate: pct !== undefined && pct !== '' ? Number(pct) / 100 : undefined,
      });
      onDecided();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this application? They can re-apply later.')) return;
    setBusyId(id); setError(null);
    try {
      await apiClient.post(`/affiliates/${id}/reject`, {});
      onDecided();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject');
    } finally {
      setBusyId(null);
    }
  };

  if (applications.length === 0) return null;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">Affiliate applications ({applications.length})</h2>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="space-y-3">
        {applications.map((a) => (
          <div key={a.id} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-medium text-gray-900">{a.name} <span className="font-normal text-gray-500">· {a.email}</span></p>
                <p className="text-xs text-gray-400 mt-0.5">Applied {new Date(a.applied_at || a.created_at).toLocaleString()}</p>
                {a.application_link && (
                  <a href={a.application_link} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline mt-1 inline-block break-all">
                    {a.application_link}
                  </a>
                )}
                {a.heard_about_us && (
                  <p className="text-xs text-gray-500 mt-1">
                    Heard about us via: {HEARD_ABOUT_US_LABELS[a.heard_about_us] || a.heard_about_us}
                    {a.heard_about_us_detail ? ` (${a.heard_about_us_detail})` : ''}
                  </p>
                )}
                {a.application_note && <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">{a.application_note}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <label className="text-xs text-gray-500 flex items-center gap-1">
                  Rate
                  <input
                    type="number" min="0" max="100" step="1" placeholder="20"
                    value={rates[a.id] ?? ''}
                    onChange={(e) => setRates({ ...rates, [a.id]: e.target.value })}
                    className="w-14 px-2 py-1 border border-gray-300 rounded text-sm"
                  />%
                </label>
                <button onClick={() => approve(a.id)} disabled={busyId === a.id} className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                  Approve
                </button>
                <button onClick={() => reject(a.id)} disabled={busyId === a.id} className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                  Reject
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CreateAffiliateForm({ onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', commission_pct: 20, payout_method: 'paypal', payout_details: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const { data } = await apiClient.post('/affiliates', {
        name: form.name,
        email: form.email,
        commission_rate: Number(form.commission_pct) / 100,
        payout_method: form.payout_method,
        payout_details: form.payout_details ? { handle: form.payout_details } : null,
        notes: form.notes,
      });
      setForm({ name: '', email: '', commission_pct: 20, payout_method: 'paypal', payout_details: '', notes: '' });
      onCreated(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create affiliate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Plus className="w-5 h-5" /> Create Affiliate</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        <label className="text-sm text-gray-600 flex items-center gap-2">
          Commission
          <input type="number" min="0" max="100" step="1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          % of each paid invoice
        </label>
        <select value={form.payout_method} onChange={(e) => setForm({ ...form, payout_method: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="paypal">PayPal</option>
          <option value="venmo">Venmo</option>
          <option value="check">Check</option>
          <option value="other">Other</option>
        </select>
        <input placeholder="Payout handle / address" value={form.payout_details} onChange={(e) => setForm({ ...form, payout_details: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        <input placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
        {saving ? 'Creating...' : 'Create affiliate'}
      </button>
    </form>
  );
}

function EditAffiliateForm({ affiliate, onSaved }) {
  const [form, setForm] = useState({
    commission_pct: Math.round((affiliate.commission_rate || 0) * 100),
    payout_method: affiliate.payout_method || 'paypal',
    payout_details: affiliate.payout_details?.handle || (typeof affiliate.payout_details === 'string' ? affiliate.payout_details : ''),
    status: affiliate.status,
    notes: affiliate.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null); setSaved(false);
    try {
      await apiClient.patch(`/affiliates/${affiliate.id}`, {
        commission_rate: Number(form.commission_pct) / 100,
        payout_method: form.payout_method,
        payout_details: form.payout_details ? { handle: form.payout_details } : null,
        status: form.status,
        notes: form.notes,
      });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async () => {
    setResending(true); setResent(false);
    try {
      await apiClient.post(`/affiliates/${affiliate.id}/resend-invite`);
      setResent(true);
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="text-sm text-gray-600 flex items-center gap-2">
          Commission
          <input type="number" min="0" max="100" step="1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          %
        </label>
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
        </select>
        <select value={form.payout_method} onChange={(e) => setForm({ ...form, payout_method: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="paypal">PayPal</option>
          <option value="venmo">Venmo</option>
          <option value="check">Check</option>
          <option value="other">Other</option>
        </select>
        <input placeholder="Payout handle / address" value={form.payout_details} onChange={(e) => setForm({ ...form, payout_details: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="px-3 py-2 border border-gray-300 rounded-lg text-sm md:col-span-2" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-3">
        <button disabled={saving} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save changes'}
        </button>
        {saved && <span className="text-sm text-green-600">Saved.</span>}
        <span className="flex-1" />
        <button type="button" onClick={resendInvite} disabled={resending} className="px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {resending ? 'Sending...' : affiliate.password_set_at ? 'Resend password reset' : 'Resend invite email'}
        </button>
        {resent && <span className="text-sm text-green-600">Sent.</span>}
      </div>
    </form>
  );
}

function AffiliateDetail({ id, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutRef, setPayoutRef] = useState('');
  const [marking, setMarking] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/affiliates/${id}`);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const markAllPaid = async () => {
    if (!window.confirm('Mark ALL unpaid commissions for this affiliate as paid?')) return;
    setMarking(true);
    try {
      await apiClient.post(`/affiliates/${id}/commissions/mark-paid`, {
        all_unpaid: true,
        payout_reference: payoutRef || null,
      });
      setPayoutRef('');
      await load();
    } finally {
      setMarking(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!data) return null;

  const { affiliate, referrals, commissions, click_funnel } = data;
  // 'held' and 'void' are deliberately excluded — the mark-paid endpoint won't
  // touch them either (see admin-affiliates.js), so this stays in sync with it.
  const unpaid = commissions.filter((c) => c.status === 'pending' || c.status === 'approved');
  const unpaidCents = unpaid.reduce((t, c) => t + (c.commission_cents || 0), 0);
  const held = commissions.filter((c) => c.status === 'held');
  const heldCents = held.reduce((t, c) => t + (c.commission_cents || 0), 0);

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4" /> Back to affiliates
      </button>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{affiliate.name}</h2>
            <p className="text-sm text-gray-600">{affiliate.email} · {pct(affiliate.commission_rate)} commission · {affiliate.status}</p>
            <p className="text-xs text-gray-500 mt-1">Payout: {affiliate.payout_method || 'n/a'} {affiliate.payout_details ? `(${affiliate.payout_details.handle || JSON.stringify(affiliate.payout_details)})` : ''}</p>
            <p className="text-xs text-gray-500 mt-1">
              {affiliate.password_set_at ? `Logged in via password since ${new Date(affiliate.password_set_at).toLocaleDateString()}` : 'Has not set a password yet (invite pending)'}
              {affiliate.last_login_at ? ` · last login ${new Date(affiliate.last_login_at).toLocaleString()}` : ''}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <CopyButton text={affiliate.link} label="Referral link" />
            <CopyButton text={`${window.location.origin}/affiliate/${affiliate.portal_token}`} label="Portal link (legacy, no login)" />
            <button onClick={() => setEditing((v) => !v)} className="text-xs text-indigo-600 hover:underline">{editing ? 'Cancel edit' : 'Edit affiliate'}</button>
          </div>
        </div>
        {editing && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <EditAffiliateForm affiliate={affiliate} onSaved={() => { setEditing(false); load(); }} />
          </div>
        )}
      </div>

      <AffiliateClickFunnel data={click_funnel} />

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Commission ledger</h3>
          <div className="flex items-center gap-3">
            {held.length > 0 && <span className="text-xs text-blue-700">{held.length} held (${money(heldCents)}): releases with their next payment</span>}
            <input placeholder="Payout reference" value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded text-sm" />
            <button onClick={markAllPaid} disabled={marking || unpaid.length === 0} className="px-3 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-50">
              Mark {unpaid.length} unpaid paid ({money(unpaidCents)})
            </button>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2">Date</th><th>Invoice</th><th>Base</th><th>Commission</th><th>Status</th><th>Paid ref</th>
            </tr>
          </thead>
          <tbody>
            {commissions.length === 0 && <tr><td colSpan="6" className="py-4 text-gray-400">No commissions yet.</td></tr>}
            {commissions.map((c) => (
              <tr key={c.id} className="border-b last:border-0">
                <td className="py-2">{new Date(c.created_at).toLocaleDateString()}</td>
                <td className="font-mono text-xs">{c.invoice_id}</td>
                <td>{money(c.base_amount_cents)}</td>
                <td className="font-medium">{money(c.commission_cents)}</td>
                <td><CommissionStatusBadge status={c.status} /></td>
                <td className="text-xs text-gray-500">{c.payout_reference || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Referred companies ({referrals.length})</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b"><th className="py-2">Company</th><th>Plan</th><th>Status</th><th>Converted</th></tr>
          </thead>
          <tbody>
            {referrals.length === 0 && <tr><td colSpan="4" className="py-4 text-gray-400">No referrals yet.</td></tr>}
            {referrals.map((r) => (
              <tr key={r.id} className="border-b last:border-0">
                <td className="py-2">{r.referee?.name || '—'}</td>
                <td className="capitalize">{r.referee?.plan || '—'}</td>
                <td><ReferralStatusBadge status={r.status} /></td>
                <td className="text-gray-500">{r.converted_at ? new Date(r.converted_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AffiliatesAdmin() {
  const [affiliates, setAffiliates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  // Independent try/catches on purpose: a failure loading applications (a
  // secondary panel) must never blank out the main affiliate list, and vice
  // versa. A single Promise.all here would let either one take the other down.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get('/affiliates');
      setAffiliates(data.affiliates || []);
    } catch (err) {
      console.error('Failed to load affiliates', err);
    } finally {
      setLoading(false);
    }
    try {
      const { data: appData } = await apiClient.get('/affiliates/applications');
      setApplications(appData.applications || []);
    } catch (err) {
      console.error('Failed to load affiliate applications', err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (selectedId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <AffiliateDetail id={selectedId} onBack={() => { setSelectedId(null); load(); }} />
      </div>
    );
  }

  const totalUnpaid = affiliates.reduce((t, a) => t + (a.unpaid_cents || 0), 0);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Affiliates</h1>
          <p className="text-gray-600 mt-1">Referral partners (non-customers), created directly or reviewed in from getsurfox.com. Recurring revenue share, manual payout.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      <ApplicationsQueue applications={applications} onDecided={load} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center justify-between">
          <div><p className="text-sm text-gray-600">Affiliates</p><p className="text-2xl font-bold">{affiliates.length}</p></div>
          <Users className="w-8 h-8 text-indigo-400" />
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center justify-between">
          <div><p className="text-sm text-gray-600">Active referrals</p><p className="text-2xl font-bold">{affiliates.reduce((t, a) => t + a.referrals_active, 0)}</p></div>
          <Check className="w-8 h-8 text-green-400" />
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg p-5 text-white flex items-center justify-between">
          <div><p className="text-sm text-indigo-100">Unpaid owed</p><p className="text-2xl font-bold">{money(totalUnpaid)}</p></div>
          <DollarSign className="w-8 h-8 text-white/80" />
        </div>
      </div>

      <CreateAffiliateForm onCreated={() => load()} />

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : affiliates.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No affiliates yet. Create one above.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b bg-gray-50">
                <th className="py-3 px-4">Name</th><th>Rate</th><th>Active</th><th>Pending</th><th>Held</th><th>Unpaid</th><th>Paid</th><th>Link</th><th></th>
              </tr>
            </thead>
            <tbody>
              {affiliates.map((a) => (
                <tr key={a.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900">{a.name}</div>
                    <div className="text-xs text-gray-500">{a.email}{a.status !== 'active' ? ` · ${a.status}` : ''}</div>
                  </td>
                  <td>{pct(a.commission_rate)}</td>
                  <td>{a.referrals_active}</td>
                  <td>{a.referrals_pending}</td>
                  <td className="text-blue-700">{a.held_cents ? money(a.held_cents) : '—'}</td>
                  <td className="font-medium text-yellow-700">{money(a.unpaid_cents)}</td>
                  <td className="text-gray-500">{money(a.paid_cents)}</td>
                  <td><CopyButton text={a.link} /></td>
                  <td><button onClick={() => setSelectedId(a.id)} className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-800 text-sm">Open <ExternalLink className="w-3 h-3" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
